import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MagnifyingGlass,
  Plus,
  IdentificationCard,
  Phone,
  Envelope,
  ArrowRight,
  CalendarBlank,
  WarningCircle,
} from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ClientFormDialog from "@/components/ClientFormDialog";
import {
  APP_LOGO,
  APP_NAME,
  APP_TAGLINE,
  PHONE_OPERATOR_LABELS,
  COUNTRY_FLAGS,
  daysUntilDay,
} from "@/lib/labels";

export default function ClientsListPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [country, setCountry] = useState("");
  const [countries, setCountries] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  const loadClients = useCallback(async (q = "", c = "") => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (c) params.birth_country = c;
      const { data } = await api.get("/clients", { params });
      setClients(data);
    } catch (e) {
      toast.error("No se pudo cargar la lista");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    try {
      const { data } = await api.get("/birth-countries");
      setCountries(data);
    } catch (e) {
      console.warn("Failed to load countries:", e);
    }
  }, []);

  useEffect(() => {
    loadClients("", "");
    loadCountries();
  }, [loadClients, loadCountries]);

  useEffect(() => {
    const t = setTimeout(() => loadClients(query.trim(), country), 220);
    return () => clearTimeout(t);
  }, [query, country, loadClients]);

  const onClientCreated = (c) => {
    setShowCreate(false);
    toast.success("Cliente creado");
    navigate(`/clients/${c.id}`);
  };

  // upcoming payments — clients with phone_payment_day within next 7 days
  const upcoming = useMemo(() => {
    return clients
      .map((c) => ({ ...c, _days: daysUntilDay(c.phone_payment_day) }))
      .filter((c) => c._days != null && c._days <= 7)
      .sort((a, b) => a._days - b._days);
  }, [clients]);

  const total = clients.length;

  return (
    <div className="min-h-screen w-full bg-white" data-testid="clients-list-page">
      <header className="border-b border-slate-200 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3">
          <img src={APP_LOGO} alt={APP_NAME} className="w-24 h-24 sm:w-28 sm:h-28 object-contain shrink-0" />
          <div className="hidden sm:block">
            <div className="font-display font-black text-lg leading-none">{APP_NAME}</div>
            <div className="label-eyebrow mt-1 text-[10px]">{APP_TAGLINE}</div>
          </div>
          <div className="flex-1" />
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 pt-10 pb-6">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow text-[#6B21A8]">·Clientes</div>
            <h1 className="font-display font-black text-4xl tracking-tight mt-1">
              Tu archivo de clientes
            </h1>
            <p className="text-sm text-slate-600 mt-2">
              {loading ? "Cargando..." : `${total} ${total === 1 ? "cliente" : "clientes"} en el sistema.`}
            </p>
          </div>
          <Button
            onClick={() => setShowCreate(true)}
            className="rounded-none bg-[#6B21A8] hover:bg-[#581C87] text-white font-bold uppercase tracking-wider px-5 h-11 btn-swap"
            data-testid="new-client-button"
          >
            <Plus size={16} weight="bold" className="mr-1" />
            Nuevo cliente
          </Button>
        </div>

        <div className="relative mt-6">
          <MagnifyingGlass
            size={18}
            weight="bold"
            className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar nombre, teléfono, # de documento..."
            className="pl-11 h-14 text-base rounded-none border-slate-300 focus-visible:ring-2 focus-visible:ring-[#6B21A8] focus-visible:ring-offset-0"
            data-testid="search-client-input"
            inputMode="search"
            autoFocus
          />
        </div>

        {/* Country filter chips */}
        {countries.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5" data-testid="country-filter">
            <button
              onClick={() => setCountry("")}
              className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 border transition-colors ${
                country === ""
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-300 text-slate-600 hover:border-slate-900"
              }`}
              data-testid="country-filter-all"
            >
              Todos
            </button>
            {countries.map((c) => {
              const active = country === c;
              return (
                <button
                  key={c}
                  onClick={() => setCountry(active ? "" : c)}
                  className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1.5 border transition-colors inline-flex items-center gap-1.5 ${
                    active
                      ? "bg-[#6B21A8] border-[#6B21A8] text-white"
                      : "bg-white border-slate-300 text-slate-700 hover:border-[#6B21A8] hover:text-[#6B21A8]"
                  }`}
                  data-testid={`country-filter-${c}`}
                >
                  <span className="text-sm leading-none">{COUNTRY_FLAGS[c] || "🌍"}</span>
                  {c}
                </button>
              );
            })}
          </div>
        )}
      </section>

      {/* Upcoming phone payments */}
      {upcoming.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-4" data-testid="upcoming-payments-section">
          <div className="flex items-center gap-2 mb-3">
            <WarningCircle size={18} weight="fill" className="text-amber-500" />
            <h2 className="font-display font-bold text-lg">Próximos pagos de teléfono</h2>
            <span className="text-xs text-slate-500 font-mono-tab">
              · siguientes 7 días
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {upcoming.map((c) => (
              <button
                key={c.id}
                onClick={() => navigate(`/clients/${c.id}`)}
                className="text-left border border-amber-300 bg-amber-50 hover:bg-amber-100 transition-colors p-3 flex items-start gap-3"
                data-testid={`upcoming-payment-${c.id}`}
              >
                <CalendarBlank size={20} weight="bold" className="text-amber-700 mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm truncate">{c.name}</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {c.phone_operator
                      ? PHONE_OPERATOR_LABELS[c.phone_operator] || c.phone_operator
                      : "—"}
                    {" · "}
                    Día {c.phone_payment_day}
                  </div>
                </div>
                <span className="warn-chip shrink-0">
                  {c._days === 0 ? "HOY" : c._days === 1 ? "MAÑANA" : `EN ${c._days}D`}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 pb-16">
        {loading && clients.length === 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-24 bg-slate-50 border border-slate-200 animate-pulse" />
            ))}
          </div>
        ) : clients.length === 0 ? (
          <div className="border border-dashed border-slate-300 p-12 text-center bg-slate-50" data-testid="empty-client-list">
            <IdentificationCard size={42} className="mx-auto text-slate-300" weight="thin" />
            <p className="mt-3 font-display font-bold text-lg">
              {query ? "Sin resultados" : "Aún no hay clientes"}
            </p>
            <p className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
              {query
                ? "Prueba con otro nombre, teléfono o número de documento."
                : "Crea tu primer cliente y sube sus documentos."}
            </p>
            {!query && (
              <button
                onClick={() => setShowCreate(true)}
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 bg-[#6B21A8] text-white text-xs font-bold uppercase tracking-wider btn-swap"
                data-testid="empty-create-client-button"
              >
                <Plus size={14} weight="bold" /> Crear el primero
              </button>
            )}
          </div>
        ) : (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="client-grid">
            {clients.map((c, idx) => {
              const days = daysUntilDay(c.phone_payment_day);
              const showDayChip = days != null;
              return (
                <li key={c.id}>
                  <button
                    onClick={() => navigate(`/clients/${c.id}`)}
                    className="group w-full text-left bg-white border border-slate-200 hover:border-slate-900 hover:shadow-[4px_4px_0_0_#6B21A8] active:scale-[0.99] transition-all p-4 sm:p-4 flex items-start gap-4"
                    data-testid={`client-row-${c.id}`}
                  >
                    <div className="font-mono-tab text-[10px] text-slate-400 mt-1 w-6 shrink-0">
                      {String(idx + 1).padStart(3, "0")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-lg truncate" data-testid={`client-name-${c.id}`}>
                        {c.name}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
                        {c.phone && (
                          <span className="inline-flex items-center gap-1">
                            <Phone size={11} weight="bold" /> {c.phone}
                          </span>
                        )}
                        {c.email && (
                          <span className="inline-flex items-center gap-1 truncate max-w-[180px]">
                            <Envelope size={11} weight="bold" /> {c.email}
                          </span>
                        )}
                        {!c.phone && !c.email && (
                          <span className="text-slate-400 italic">Sin datos</span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            className="text-[10px] font-bold px-1.5 py-0.5 bg-slate-900 text-white tabular-nums"
                            data-testid={`client-doc-count-${c.id}`}
                          >
                            {c.doc_count ?? 0} DOC
                          </span>
                          {c.birth_country && (
                            <span className="brand-chip">
                              <span className="text-sm leading-none">
                                {COUNTRY_FLAGS[c.birth_country] || "🌍"}
                              </span>
                              {c.birth_country}
                            </span>
                          )}
                          {c.phone_operator && (
                            <span className="brand-chip">
                              {PHONE_OPERATOR_LABELS[c.phone_operator] || c.phone_operator}
                            </span>
                          )}
                          {showDayChip && (
                            <span className={days <= 3 ? "warn-chip" : "brand-chip"}>
                              Día {c.phone_payment_day}
                            </span>
                          )}
                        </div>
                        <ArrowRight
                          size={16}
                          weight="bold"
                          className="text-slate-400 group-hover:text-[#6B21A8] group-hover:translate-x-1 transition-all"
                        />
                      </div>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <ClientFormDialog open={showCreate} onOpenChange={setShowCreate} onSaved={onClientCreated} />

      {/* Floating Action Button - mobile-first quick add */}
      <button
        onClick={() => setShowCreate(true)}
        className="fab safe-bottom sm:hidden"
        style={{ bottom: "20px", right: "20px" }}
        title="Nuevo cliente"
        data-testid="fab-new-client"
      >
        <Plus size={26} weight="bold" />
      </button>
    </div>
  );
}
